import { Grid, InputAdornment } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { Ref, forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FaDollarSign } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { IJobValuesUpdateByUfCodesInputModel, IJobValuesUpdateInputModel } from 'inputModels';
import { IValues } from 'entities';
import { toMoney } from 'vanilla-masker';
import { AccordionExpanded } from '../../../components/AccordionExpanded';
import CurrencyInput from '../../../components/CurrencyInput';
import { useApp, useAuth } from '../../../context';
import { TypeUser } from '../../../types/enums';
import { ButtonPopover, DataButtonPopover } from '../ButtonPopover';
import { LineUpRight, Panel } from '../styles';
import { UserController } from '../../../controller/UserController';
import { useAlert } from '../../../hooks/useAlert';
import { useCorrespondences } from '../../../context/correspondences';
import { useJobValues } from '../../../context/jobvalues';
import { BRAZILIAN_STATES } from '../../../constants';
import { formatValueFromInputMoney } from '../../../utils/formatValueFromInputMoney';
import { correspondenceMock } from '../../../constants/Tour/Mocks/myValuesMock';

type Data = {
  ufCode: number;
};
export interface IRef {
  handleUpdate: () => void;
}

type Props = {
  data: Data;
  loading: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  isTutorial?: boolean;
};

export const ServiceValues = forwardRef<IRef, Props>(
  ({ data: { ufCode }, onOpen, onClose, loading, isTutorial = false }: Props, ref: Ref<IRef>) => {
    const app = useApp();
    const { user } = useAuth();
    const alert = useAlert();
    const { setJobValuesByUser, stateSelected, jobValuesByUser } = useJobValues();
    const { correspondencesSelectedByUser } = useCorrespondences();
    const [statesSelected, setStatesSelected] = useState<DataButtonPopover[]>([]);
    const { control, getValues, setValue, setError } = useFormContext();
    const handleOnItemsChecked = (items: DataButtonPopover[]) => {
      setStatesSelected([...items]);
    };

    const handleApply = () => {
      if (!statesSelected.length) {
        alert.show({
          type: 'error',
          message: 'Selecione pelo menos um estado para aplicar os valores',
        });
        return;
      }

      app.setLoading(true);
      const values: IValues = {};
      let hasError = false;

      correspondencesSelectedByUser.forEach((correspondence) => {
        const raw = getValues(ufCode + correspondence.id);
        const value = parseFloat(String(raw).replace(',', '.') || '0');
        if (value > 0 && value < 1) {
          setError(ufCode + correspondence.id, {
            type: 'manual',
            message: 'O valor mínimo é R$ 1,00',
          });
          hasError = true;
        } else {
          Object.assign(values, { [correspondence.id]: value ?? 0 });
        }
      });

      if (hasError) {
        app.setLoading(false);
        return;
      }

      const updateByListUfCodes: IJobValuesUpdateByUfCodesInputModel = {
        ufCodes: [ufCode, ...statesSelected.map((state) => Number(state.id))],
        values,
      };

      UserController.updateJobValuesByUfCodes(updateByListUfCodes)
        .then(() => {
          alert.show({ type: 'success', message: 'Valores atualizados com sucesso!' });
          setJobValuesByUser((prev) => {
            const newValuesByUfCode = { ...prev?.valuesByUfCode };
            updateByListUfCodes.ufCodes.forEach((code) => {
              newValuesByUfCode[code] = values;
            });
            return {
              ...prev,
              valuesByUfCode: newValuesByUfCode,
            };
          });
        })
        .catch(() => {
          alert.show({
            type: 'error',
            message: 'Houve um erro ao atualizar valores, tente novamente',
          });
        })
        .finally(() => app.setLoading(false));
    };

    const handleUpdate = useCallback(() => {
      if (!stateSelected) {
        alert.show({ type: 'error', message: 'Selecione um estado para aplicar os valores' });
        return;
      }
      app.setLoading(true);

      const values: IValues = {};
      let hasError = false;

      correspondencesSelectedByUser.forEach((c) => {
        const raw = getValues(ufCode + c.id);
        const value = parseFloat(String(raw).replace(',', '.') || '0');

        if (value > 0 && value < 1) {
          setError(ufCode + c.id, {
            type: 'manual',
            message: 'O valor mínimo é R$ 1,00',
          });
          hasError = true;
        } else {
          Object.assign(values, { [c.id]: value ?? 0 });
        }
      });

      if (hasError) {
        app.setLoading(false);
        return;
      }

      const updateJobValues: IJobValuesUpdateInputModel = {
        ufCode: stateSelected.ufCode,
        values,
      };

      UserController.updateJobValues(updateJobValues)
        .then(() => {
          alert.show({ type: 'success', message: 'Valores atualizados com sucesso!' });
          setJobValuesByUser((prev) => ({
            ...prev,
            valuesByUfCode: {
              ...prev?.valuesByUfCode,
              [stateSelected.ufCode]: values,
            },
          }));
        })
        .catch(() => {
          alert.show({
            type: 'error',
            message: 'Houve um erro ao atualizar valores, tente novamente',
          });
        })
        .finally(() => {
          app.setLoading(false);
        });
    }, [
      alert,
      app,
      correspondencesSelectedByUser,
      getValues,
      setError,
      setJobValuesByUser,
      stateSelected,
      ufCode,
    ]);

    useImperativeHandle(ref, () => ({
      handleUpdate,
    }));

    useEffect(() => {
      if (jobValuesByUser?.valuesByUfCode === undefined) return;
      correspondencesSelectedByUser?.forEach((correspondence) => {
        const value = jobValuesByUser.valuesByUfCode?.[ufCode]?.[correspondence?.id];
        const valueFormated = formatValueFromInputMoney(
          value?.toString()?.replace('.', ',') ?? '0',
        );
        setValue(ufCode + correspondence.id, toMoney(valueFormated));
      });
    }, [correspondencesSelectedByUser, jobValuesByUser?.valuesByUfCode, setValue, ufCode]);

    return (
      <Panel>
        {loading ? (
          <Grid container justifyContent="center" alignContent="center" minHeight="5rem">
            <CircularProgress size={24} />
          </Grid>
        ) : (
          <AccordionExpanded
            className="container-accordion-expanded"
            title="Valores dos Serviços"
            onOpen={onOpen}
            defaultOpen
            onClose={onClose}
            icon>
            <div
              id="my-values"
              className={
                loading || correspondencesSelectedByUser.length === 0
                  ? 'center'
                  : 'container-services'
              }>
              {isTutorial
                ? correspondenceMock.map((correspondence) => (
                    <div className="services" key={correspondence.id} title={correspondence.name}>
                      <div className="inputMoney">
                        <p className="correspondenceName">{correspondence.name}</p>
                        <Controller
                          control={control}
                          name={ufCode + correspondence.id}
                          render={({ field: { onChange, value } }) => (
                            <CurrencyInput
                              value={value ?? 0}
                              variant="outlined"
                              size="small"
                              className="inputSuggestionValue"
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment sx={{ pl: 1 }} position="start">
                                    R$
                                  </InputAdornment>
                                ),
                              }}
                              onChange={onChange}
                            />
                          )}
                        />
                      </div>
                    </div>
                  ))
                : correspondencesSelectedByUser.length
                ? correspondencesSelectedByUser.map((correspondence) => (
                    <div className="services" key={correspondence.id} title={correspondence.name}>
                      <div className="inputMoney">
                        <p className="correspondenceName">{correspondence.name}</p>
                        <div className="inputMoneyValue">
                          <Controller
                            control={control}
                            name={ufCode + correspondence.id}
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                              <CurrencyInput
                                value={value ?? 0}
                                variant="outlined"
                                size="small"
                                className="inputSuggestionValue"
                                errorMessage={error?.message}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment sx={{ pl: 1 }} position="start">
                                      R$
                                    </InputAdornment>
                                  ),
                                }}
                                onChange={(e) => {
                                  onChange(e);
                                  if (error) {
                                    setError(ufCode + correspondence.id, {
                                      type: 'manual',
                                      message: '',
                                    });
                                  }
                                }}
                              />
                            )}
                          />
                        </div>
                      </div>
                    </div>
                  ))
                : user.type !== TypeUser.COLLABORATOR && (
                    <div className="empty-correspondences">
                      Você não tem correspondências cadastradas,{' '}
                      <Link
                        to={
                          [TypeUser.COMPANY, TypeUser.ADM].includes(user.type)
                            ? '/legalPersonAccount'
                            : '/naturalPersonAccount'
                        }>
                        clique aqui
                      </Link>{' '}
                      para cadastrar
                    </div>
                  )}
            </div>
            <LineUpRight />
            <div className="container-apply-values" id="apply-values">
              <div className="apply-values-title">
                <FaDollarSign />
                <p>Aplicar valores</p>
              </div>
              <div className="apply-values-specialties-city">
                {stateSelected && (
                  <ButtonPopover
                    title="Estados"
                    defaultValue={{ id: stateSelected.ufCode, name: stateSelected.name }}
                    data={BRAZILIAN_STATES.filter((s) => s.ufCode !== ufCode).map((state) => ({
                      id: state.ufCode,
                      name: state.name,
                    }))}
                    onItemsChecked={(items) => handleOnItemsChecked(items)}
                  />
                )}
              </div>
              <div className="container-apply">
                <button id="apply-values-button" type="button" onClick={() => handleApply()}>
                  Aplicar
                </button>
              </div>
            </div>
          </AccordionExpanded>
        )}
      </Panel>
    );
  },
);
